import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { generateWorkSteps } from '../../services/geminiService';
import { DokumentenUpload } from './DokumentenUpload';
import { KiSchrittVorschlag, PlanZeitraum, baueKiZeilen, beschreibungFuerKi, kiAntwortAlsObjekt } from './uebernahme';
import type { BeschreibungVorschlag, SigeHinweis } from './planungAnbindung';
import { PlanungHinweise } from './PlanungHinweise';
import { Button } from '../Button';
import { Bot, CheckSquare, ClipboardPaste, FileUp, ListPlus, Loader2, Square } from 'lucide-react';

// Schritt 2 des Anlage-Assistenten: Grundlagen einlesen (optional).
// Drei Wege — Dokument, Beschreibung, oder überspringen und mit dem Muster starten.
// Übernommen wird nie ungefragt: jeder Vorschlag ist einzeln abwählbar.
// Die Baubeschreibung bleibt flüchtiger Zustand dieses Schritts; die
// Planungsphase bietet ihre Beschreibung nur als Vorbelegung per Knopf an.

interface Props {
  sigeProjektId: string;
  zeitraum: PlanZeitraum;
  /** Bauzeitenplan liegt noch nicht vor: KI-Vorschläge werden ohne Zeitraum übernommen. */
  ohneZeitachse?: boolean;
  hauptabschnitte: string[];
  vorhandeneBezeichnungen: string[];
  startSortierung: number;
  /** Baubeschreibung aus dem Projektsteckbrief — Vorschlag, solange das Feld leer ist. */
  beschreibungVorschlag: BeschreibungVorschlag | null;
  /** Beantwortete Fragen der Planungsphase mit Bezug zum SiGe-Plan. */
  hinweise: readonly SigeHinweis[];
  onHinweisBeruecksichtigt: (hinweis: SigeHinweis) => Promise<void>;
  onUebernommen: () => void | Promise<void>;
  onMitMusterWeiter: () => void;
  melde: (text: string, ton?: 'info' | 'fehler') => void;
}

export const WizardStepGrundlagen: React.FC<Props> = ({
  sigeProjektId, zeitraum, ohneZeitachse = false, hauptabschnitte, vorhandeneBezeichnungen, startSortierung,
  beschreibungVorschlag, hinweise, onHinweisBeruecksichtigt,
  onUebernommen, onMitMusterWeiter, melde,
}) => {
  const [uploadOffen, setUploadOffen] = useState(false);
  const [beschreibung, setBeschreibung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [vorschlaege, setVorschlaege] = useState<KiSchrittVorschlag[] | null>(null);
  const [auswahl, setAuswahl] = useState<number[]>([]);

  const vorschlagen = async () => {
    // Unmittelbar vor dem KI-Aufruf: Namen, Telefonnummern und E-Mails raus —
    // gleich ob der Text getippt oder aus der Planungsphase übernommen wurde.
    const prompt = beschreibungFuerKi(beschreibung);
    if (!prompt) return;
    setLaeuft(true);
    try {
      const antwort = await generateWorkSteps(prompt, zeitraum.von);
      const daten = kiAntwortAlsObjekt(antwort);
      const liste: KiSchrittVorschlag[] = Array.isArray(daten?.arbeitsschritte) ? daten.arbeitsschritte : [];
      if (liste.length === 0) {
        melde('Die KI hat zu dieser Beschreibung keine Arbeitsschritte geliefert. Bitte das Bauvorhaben genauer beschreiben.', 'fehler');
        return;
      }
      setVorschlaege(liste);
      setAuswahl(liste.map((_, i) => i));
    } catch (e: any) {
      console.error('Arbeitsschritt-Vorschlag fehlgeschlagen', e);
      melde(`Vorschlag fehlgeschlagen: ${e?.message || String(e)}`, 'fehler');
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async () => {
    if (!vorschlaege) return;
    setSpeichert(true);
    try {
      const gewaehlt = vorschlaege.filter((_, i) => auswahl.includes(i));
      const { rows, duplikate } = baueKiZeilen(gewaehlt, {
        sigeProjektId,
        zeitraum,
        ohneZeitraeume: ohneZeitachse,
        startSortierung,
        bekannteAbschnitte: hauptabschnitte,
        vorhandeneBezeichnungen,
      });
      if (rows.length === 0) {
        melde('Nichts übernommen — die Auswahl steht bereits im Plan oder hat keine Bezeichnung.', 'fehler');
        return;
      }
      const { error } = await supabase.from('sige_arbeitsschritte').insert(rows);
      if (error) throw error;
      setVorschlaege(null);
      setAuswahl([]);
      setBeschreibung('');
      melde(
        `${rows.length} Arbeitsschritt(e) übernommen.` +
        (duplikate.length ? ` ${duplikate.length} bereits vorhandene ausgelassen.` : ''),
      );
      await onUebernommen();
    } catch (e: any) {
      console.error('Übernahme der KI-Arbeitsschritte fehlgeschlagen', e);
      melde(`Übernahme fehlgeschlagen: ${e?.message || String(e)}`, 'fehler');
    } finally {
      setSpeichert(false);
    }
  };

  const umschalten = (i: number) =>
    setAuswahl(a => (a.includes(i) ? a.filter(x => x !== i) : [...a, i]));

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        Optional: vorhandene Unterlagen oder eine kurze Beschreibung auswerten lassen. Alles hier ist
        überspringbar — die Muster-Vorlage im nächsten Schritt genügt für einen vollständigen Plan.
      </p>

      <PlanungHinweise hinweise={hinweise} onBeruecksichtigt={onHinweisBeruecksichtigt} standardOffen />

      {/* A — Dokument */}
      <div className="border border-slate-200 rounded-xl p-4 bg-white">
        <div className="flex items-start gap-3">
          <div className="bg-blue-50 border border-blue-100 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
            <FileUp className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800 text-sm">Dokument hochladen</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Bauzeitenplan, Leistungsverzeichnis oder Beteiligtenliste (PDF, XLSX, DOCX). Die
              gefundenen Arbeitsschritte werden vor der Übernahme angezeigt.
            </p>
          </div>
          <Button variant="secondary" className="shrink-0 text-sm" onClick={() => setUploadOffen(true)}>
            Datei auswählen
          </Button>
        </div>
      </div>

      {/* B — Beschreibung */}
      <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-3">
        <div className="flex items-start gap-3">
          <div className="bg-violet-50 border border-violet-100 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-violet-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800 text-sm">Bauvorhaben beschreiben</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Zwei bis drei Sätze zu Art, Größe und Besonderheiten des Bauvorhabens — daraus werden
              5 bis 15 Arbeitsschritte mit Gewerk und Zeitraum vorgeschlagen.
            </p>
          </div>
        </div>
        <textarea
          data-tutorial="sigeplan.beschreibung"
          className="w-full text-sm p-3 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 min-h-[90px]"
          placeholder="z. B. Neubau einer dreigeschossigen Lagerhalle in Stahlbeton mit Flachdach, Kranmontage, Fassade aus Sandwichelementen, Außenanlagen mit Zufahrt."
          value={beschreibung}
          onChange={e => setBeschreibung(e.target.value)}
        />
        {!beschreibung.trim() && beschreibungVorschlag && (
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] text-blue-900 leading-snug">
                <span className="font-bold">Aus der Planungsphase:</span>{' '}
                <span className="line-clamp-2" title={beschreibungVorschlag.text}>{beschreibungVorschlag.text}</span>
              </div>
              <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                <span className="font-bold">Quelle:</span> {beschreibungVorschlag.quelle}
                {beschreibungVorschlag.grund && <> — {beschreibungVorschlag.grund}</>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBeschreibung(beschreibungVorschlag.text)}
              title="Die Beschreibung in das Feld übernehmen — gespeichert wird hier nichts"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border text-blue-700 bg-white border-blue-300 hover:bg-blue-100 shrink-0"
            >
              <ClipboardPaste className="w-3.5 h-3.5" /> Übernehmen
            </button>
          </div>
        )}
        <div className="flex justify-end">
          <Button data-tutorial="sigeplan.arbeitsschritte-ki" variant="ai" onClick={vorschlagen} disabled={laeuft || !beschreibung.trim()} className="text-sm">
            {laeuft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
            {laeuft ? 'Wird erstellt …' : 'Arbeitsschritte vorschlagen'}
          </Button>
        </div>

        {vorschlaege && (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Vorschläge ({auswahl.length} von {vorschlaege.length} ausgewählt)
              </h4>
              <button
                onClick={() => setAuswahl(auswahl.length === vorschlaege.length ? [] : vorschlaege.map((_, i) => i))}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {auswahl.length === vorschlaege.length ? 'Keine' : 'Alle'}
              </button>
            </div>
            {ohneZeitachse && (
              <p className="text-[11px] text-slate-500">
                Bauzeitenplan liegt noch nicht vor — die Schritte werden ohne Zeitraum übernommen.
              </p>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto p-0.5">
              {vorschlaege.map((v, idx) => {
                const gewaehlt = auswahl.includes(idx);
                return (
                  <div
                    key={idx}
                    onClick={() => umschalten(idx)}
                    className={`p-3 border rounded-xl flex items-start gap-3 cursor-pointer transition-colors ${
                      gewaehlt ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="mt-0.5 shrink-0">
                      {gewaehlt ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-slate-300" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 text-sm">
                        {v.bezeichnung}
                        {v.kritisch && <span className="ml-2 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">kritisch</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {v.gewerk && <span className="mr-3">Gewerk: {v.gewerk}</span>}
                        {/* Ohne Zeitachse wird der Zeitraum nicht übernommen — also auch nicht angeboten. */}
                        {!ohneZeitachse && (v.zeitraumVon || v.zeitraumBis) && <span>{v.zeitraumVon || '?'} – {v.zeitraumBis || '?'}</span>}
                      </div>
                      {!!v.vorschriften?.length && (
                        <div className="text-[11px] text-slate-400 mt-1 truncate">{v.vorschriften.join(' · ')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => { setVorschlaege(null); setAuswahl([]); }} disabled={speichert}>
                Verwerfen
              </Button>
              <Button variant="primary" onClick={uebernehmen} disabled={speichert || auswahl.length === 0}>
                {speichert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ListPlus className="w-4 h-4 mr-2" />}
                Ausgewählte übernehmen ({auswahl.length})
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* C — Überspringen */}
      <div className="border border-dashed border-slate-300 rounded-xl p-4 bg-slate-50 flex items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-slate-700 text-sm">Ohne Vorlage starten</h3>
          <p className="text-xs text-slate-500 mt-0.5">Direkt weiter zu den Standard-Abschnitten des Musterplans.</p>
        </div>
        <Button variant="secondary" className="shrink-0 text-sm" onClick={onMitMusterWeiter}>
          Überspringen — mit Musterplan starten
        </Button>
      </div>

      {uploadOffen && (
        <DokumentenUpload
          sigeProjektId={sigeProjektId}
          projektVon={zeitraum.von}
          projektBis={zeitraum.bis}
          startSortierung={startSortierung}
          hauptabschnitte={hauptabschnitte}
          vorhandeneBezeichnungen={vorhandeneBezeichnungen}
          onClose={() => setUploadOffen(false)}
          onResultsApplied={() => { void onUebernommen(); }}
        />
      )}
    </div>
  );
};
